import React, { useEffect, useRef, useState, useMemo } from "react";
import { ChevronDown, Check, Search } from "lucide-react";

// A single-select dropdown with type-ahead filtering — built for the large
// 288-value time lists (type "08:1" to jump straight to 08:15).
export function SearchableSelect({ label, value, onChange, options, placeholder = "Select", testid }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const h = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    if (open) { setQuery(""); setTimeout(() => inputRef.current?.focus(), 20); }
  }, [open]);

  const selected = options.find((o) => o.value === value);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  }, [query, options]);

  const pick = (v) => { onChange(v); setOpen(false); };

  return (
    <div className="block" ref={ref}>
      {label && <span className="block text-[11px] uppercase tracking-wider text-muted mb-1.5">{label}</span>}
      <div className="relative">
        <button type="button" data-testid={testid} onClick={() => setOpen((o) => !o)}
          className="w-full h-9 px-3 flex items-center justify-between gap-2 bg-surface2 border border-line rounded-sm text-sm text-ink transition-colors duration-150 hover:border-lineFocus focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/40">
          <span className={selected ? "text-ink mono" : "text-faint"}>{selected ? selected.label : placeholder}</span>
          <ChevronDown className="w-4 h-4 text-muted shrink-0" />
        </button>
        {open && (
          <div className="absolute z-50 mt-1 w-full bg-surface2 border border-lineFocus rounded-sm shadow-2xl animate-fadeup overflow-hidden">
            <div className="flex items-center gap-2 px-2.5 py-1.5 border-b border-line">
              <Search className="w-3.5 h-3.5 text-muted shrink-0" />
              <input ref={inputRef} value={query} onChange={(e) => setQuery(e.target.value)}
                data-testid={testid ? `${testid}-search` : undefined}
                onKeyDown={(e) => { if (e.key === "Enter" && filtered[0]) { e.preventDefault(); pick(filtered[0].value); } }}
                placeholder="Type to filter…" className="flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-faint mono" />
            </div>
            <div className="max-h-56 overflow-auto">
              {filtered.length === 0 && <div className="px-3 py-2 text-xs text-faint">No match</div>}
              {filtered.map((o) => (
                <button key={o.value} type="button"
                  data-testid={testid ? `${testid}-opt-${o.value}` : undefined}
                  onClick={() => pick(o.value)}
                  className={`w-full px-3 py-1.5 flex items-center justify-between text-left text-sm mono transition-colors duration-100 ${o.value === value ? "bg-primary/15 text-ink" : "text-muted hover:bg-primary/10 hover:text-ink"}`}>
                  <span>{o.label}</span>
                  {o.value === value && <Check className="w-3.5 h-3.5 text-primary" />}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
